import { Button, Card, Checkbox, Descriptions } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React, { useContext, useEffect, useState } from 'react';
import { deviceAdminApi } from '../../../../../api';
import { ResponseObject, SelectOptions } from '../../../../../model/CommonModel';
import { DownloadTSFirmware } from '../../../../../model/DeviceAdminModel';
import { InformationMessages, Modal, Select } from '../../../../common';
import { NotificationStatus } from '../../../../common/NotificationStatus/NotificationStatus';
import { DoorStoreSingleContext } from '../../DoorModal/DoorStoreSingleContext';
import { setApplyToAllReaders, setTSFirmware, StoreContext } from '../contextReader';
import styles from './updateFirmwareModal.module.scss';

type Props = {
	visible: boolean;
	closeModal: () => void;
	handleDownloadFirmware: () => void;
	useDoorContext: boolean;
};

const UpdateFirmwareModal: React.FC<Props> = ({ handleDownloadFirmware, visible, closeModal, useDoorContext }) => {
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [firmwares, setFirmwares] = useState<SelectOptions<string>[]>([]);
	const [warningMessage, setWarningMessage] = useState<JSX.Element>(<></>);

	if (useDoorContext) {
		var {
			contextStateDoor: {
				entryReader: {
					firmwareTitle,
					selections: { tSFirmware },
					tSFirmwares,
					firmwareWarning,
					applyToAllReaders,
					name,
					id,
					index,
					controllerId,
				},
			},
			dispatcherDoor,
		} = useContext(DoorStoreSingleContext);
	} else {
		var {
			readerState: {
				firmwareTitle,
				selections: { tSFirmware },
				tSFirmwares,
				firmwareWarning,
				applyToAllReaders,
				name,
				id,
				index,
				controllerId,
			},
			dispatcher,
		} = useContext(StoreContext);
	}

	useEffect(() => {
		const messages: string[] = firmwareWarning.split('\n');
		setFirmwares(tSFirmwares.map(x => ({ label: x.Name, value: x.Id.toString(), id: x.Id.toString() })));
		setWarningMessage(<InformationMessages messages={messages} />);
	}, [tSFirmwares]);

	const handleUpdateFirmware = () => {
		setDownloadLoading(true);
		const tsFirmwareInfo: DownloadTSFirmware = {
			ControllerId: controllerId,
			ReaderIndex: index,
			ReaderId: id,
			ReaderName: name,
			FirmwareId: tSFirmware,
			ApplyToAllReaders: applyToAllReaders,
		};
		deviceAdminApi.downloadFirmware(tsFirmwareInfo).then((res: ResponseObject) => {
			NotificationStatus({
				responseData: res,
				onSuccessCallback: () => {
					handleDownloadFirmware();
					closeModal();
				},
			});
			setDownloadLoading(false);
		});
	};

	const handleOnSetTSFirmware = (value: string) => {
		useDoorContext ? dispatcherDoor(setTSFirmware(Number(value))) : dispatcher(setTSFirmware(Number(value)));
	};

	const handleOnApplyToAllReaders = (value: CheckboxChangeEvent) => {
		useDoorContext ? dispatcherDoor(setApplyToAllReaders(value.target.checked)) : dispatcher(setApplyToAllReaders(value.target.checked));
	};

	return (
		<Modal
			visible={visible}
			title={firmwareTitle}
			onClickOk={handleUpdateFirmware}
			onCancel={closeModal}
			width={'900px'}
			footer={[
				<Button
					id='updateFirmwareModalDownloadButton'
					key='download'
					type='primary'
					loading={downloadLoading}
					onClick={handleUpdateFirmware}
					disabled={tSFirmware === 0}>
					{_('Download')}
				</Button>,
				<Button id='updateFirmwareModalCancelButton' key='cancel' onClick={closeModal}>
					{_('Cancel')}
				</Button>,
			]}>
			<div className={styles.firmwareModal} id='updateFirmwareModal'>
				<p>{_('SelectFirmware')}</p>
				<Select
					className={styles.select}
					options={firmwares}
					onChange={handleOnSetTSFirmware}
					key='tsFirmware'
					id='updateFirmwareModalSelectFirmwareSelect'
					value={tSFirmware.toString()}
					getPopupContainer={() => document.getElementById('updateFirmwareModal')}
				/>
			</div>
			<div className={styles.firmwareCard}>
				<Card>
					<Descriptions>
						<Descriptions.Item>{warningMessage}</Descriptions.Item>
					</Descriptions>
				</Card>
			</div>
			<div className={styles.firmwareModal}>
				<Checkbox checked={applyToAllReaders} onChange={handleOnApplyToAllReaders} id={'applyFirmware'}>
					<span>{_('ApplyFirmwareToAllReaders')}</span>
				</Checkbox>
			</div>
		</Modal>
	);
};

export { UpdateFirmwareModal };
